import React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Actions from 'webapp/actions/actions';
import CopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import Button from '@mui/material/Button';

const concordServicerNumberPrefix = '148001';

const HelpIdsTable = ({ eligibility, customer, proposal, financingRequest, financingMethod, onCloseClick }) => {
  const [copied, setCopied] = React.useState(false);
  const [installer, setInstaller] = React.useState(null);
  const applicationIds = eligibility
    ? JSON.parse(eligibility?.creditCheck?.financingProducts[0]?.externalLenderId)
    : null;
  const choiceId = applicationIds ? Object.assign(...applicationIds.filter((id) => id.key === 'Choice')) : '';
  const zeroId = applicationIds ? Object.assign(...applicationIds.filter((id) => id.key === 'Zero')) : '';

  /* eslint-disable  react-hooks/exhaustive-deps */
  React.useEffect(() => Actions.fetchUserByCustomer(customer.id).then((data) => setInstaller(data)), []);
  /* eslint-enable  react-hooks/exhaustive-deps */

  const getStepIds = () => {
    return financingRequest?.steps
      .map((step) => {
        return {
          [`stepId${step.externalSku}`]: `Step ID [${step.id}] Doc IDs [${step?.docs
            ?.map((doc) => doc.id)
            ?.join(', ')}]`,
        };
      })
      .reduce((acc, cur) => ({ ...acc, ...cur }), {});
  };

  const getRevisionIds = () => {
    return financingRequest?.steps
      .map((step) => {
        return {
          [`currentRevisionId${step.externalSku}`]: `${step?.docs?.map((doc) => doc?.currentRevision?.id)?.join(', ')}`,
        };
      })
      .reduce((acc, cur) => ({ ...acc, ...cur }), {});
  };

  const getEnvelopeIds = () => {
    return financingRequest?.steps
      .map((step) => {
        return {
          [`envelopeId${step.externalSku}`]: `${step?.docs
            ?.map((doc) => doc?.currentRevision?.envelopeId)
            ?.join(', ')}`,
        };
      })
      .reduce((acc, cur) => ({ ...acc, ...cur }), {});
  };

  const IDS = {
    zeroId: zeroId ? zeroId?.value : '',
    choiceId: choiceId ? choiceId.value : '',
    creditCheckId: customer?.currentCreditCheckId,
    customerId: customer?.id,
    loanNumber: proposal?.lastApprovedHomeownerRevisionId,
    concordServicingNumber: concordServicerNumberPrefix + proposal?.lastApprovedHomeownerRevisionId,
    ownerUsername: installer?.username,
    ownerCompanyName: installer?.company.name,
    customerFullname: customer?.cfirstname + ' ' + customer?.clastname,
    customerPhone: customer?.cphone1 + ' OptIn [' + (customer.cphone1smsOptIn ? 'true' : 'false') + ']',
    customerEmail: customer?.cemail,
    coappFullname: customer?.csecondaryfirstname + ' ' + customer?.csecondarylastname,
    coappPhone: customer?.csecondaryphone,
    coappEmail: customer?.csecondaryemail,
    customerInstallAddress:
      customer?.caddress1 + ', ' + customer?.ccity + ', ' + customer?.cstate + ' ' + customer?.czip,
    apr: financingMethod?.apr,
    amountFinanced: financingMethod?.amountFinanced,
    loanType: financingMethod?.loanType,
    term: financingMethod?.term,
    bonitaCaseId: eligibility ? eligibility?.creditCheck?.financingProducts[0]?.externalId : '',
    coid: proposal?.coid,
    frid: financingRequest ? financingRequest.id : '',
    fmid: financingMethod?.id,
    proposalId: proposal?.id,
    installerId: installer?.id,
    ...getStepIds(),
    ...getRevisionIds(),
    ...getEnvelopeIds(),
  };

  // handleCopy function to copy IDS to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(IDS)).then(
      () => {
        setCopied(true);
      },
      () => {
        console.error('Failed to copy to clipboard');
      }
    );
  };

  return (
    <Box>
      <Box>
        <Box className="modal-header" display="flex" justifyContent="space-between" px="30px" alignItems="center">
          <Box className="header-title">{'Help Id: ' + sessionStorage.getItem('fetchTraceId')}</Box>
          <Button
            id={'qa-copy-help-ids'}
            variant="text"
            startIcon={copied ? <CheckIcon /> : <CopyIcon />}
            className={copied && 'copied'}
            onClick={handleCopy}
          >
            {copied ? 'IDs Copied!' : 'Copy IDs'}
          </Button>
        </Box>
        <List id="qa-application-info" className="help-ids-list">
          <Divider textAlign="left">
            <b>APPLICATION INFO</b>
          </Divider>
          {zeroId?.value && (
            <ListItem id={'qa-help-application-id-zero'}>
              <Box mr={1}>
                <b>Application ID (12-3/Zero):</b>
              </Box>
              <Box component="span">{zeroId?.value}</Box>
            </ListItem>
          )}
          {choiceId?.value && (
            <ListItem id={'qa-help-application-id-choice'}>
              <Box mr={1}>
                <b>Application ID (LevelPay/Choice):</b>
              </Box>
              <Box component="span">{choiceId?.value}</Box>
            </ListItem>
          )}
          <ListItem id={'qa-help-cc-id'}>
            <Box mr={1}>
              <b>Credit Check ID: </b>
            </Box>
            <Box component="span">{customer?.currentCreditCheckId}</Box>
          </ListItem>
          <ListItem id={'qa-help-cust-id'}>
            <Box mr={1}>
              <b>Customer ID: </b>
            </Box>
            <Box component="span">{customer.id}</Box>
          </ListItem>
          <ListItem id={'qa-help-loan-number'}>
            <Box mr={1}>
              <b>Loan Number: </b>
            </Box>
            <Box component="span">{proposal?.lastApprovedHomeownerRevisionId}</Box>
          </ListItem>
          <ListItem id={'qa-help-concord-servicing-number'}>
            <Box mr={1}>
              <b>Concord Servicing Number: </b>
            </Box>
            <Box component="span">
              {proposal?.lastApprovedHomeownerRevisionId
                ? concordServicerNumberPrefix + proposal?.lastApprovedHomeownerRevisionId
                : ''}
            </Box>
          </ListItem>
          <ListItem id={'qa-help-owner-name'}>
            <Box mr={1}>
              <b>Installer User Name (owner): </b>
            </Box>
            <Box component="span">{installer?.username}</Box>
          </ListItem>
          <ListItem id={'qa-help-owner-company'}>
            <Box mr={1}>
              <b>Installer Company: </b>
            </Box>
            <Box component="span">{installer?.company.name}</Box>
          </ListItem>
        </List>
        <List id="qa-customer-info" className="help-ids-list">
          <Divider textAlign="left">
            <b>CUSTOMER INFO</b>
          </Divider>
          <ListItem id={'qa-help-primary-fullname'}>
            <Box mr={1}>
              <b>Primary Name:</b>
            </Box>
            <Box component="span">
              {customer.cfirstname} {customer.clastname}
            </Box>
          </ListItem>
          <ListItem id={'qa-help-primary-phone'}>
            <Box mr={1}>
              <b>Primary Phone:</b>
            </Box>
            <Box component="span">
              {customer.cphone1} OptIn [{customer.cphone1smsOptIn ? 'true' : 'false'}]
            </Box>
          </ListItem>
          <ListItem id={'qa-help-primary-email'}>
            <Box mr={1}>
              <b>Primary Email:</b>
            </Box>
            <Box component="span">{customer.cemail}</Box>
          </ListItem>
          {customer.csecondarylastname && (
            <>
              <ListItem id={'qa-help-coapp-fullname'}>
                <Box mr={1}>
                  <b>Co-Applicant Name:</b>
                </Box>
                <Box component="span">
                  {customer.csecondaryfirstname} {customer.csecondarylastname}
                </Box>
              </ListItem>
              <ListItem id={'qa-help-coapp-phone'}>
                <Box mr={1}>
                  <b>Co-Applicant Phone:</b>
                </Box>
                <Box component="span">{customer.csecondaryphone}</Box>
              </ListItem>
              <ListItem id={'qa-help-coapp-email'}>
                <Box mr={1}>
                  <b>Co-Applicant Email:</b>
                </Box>
                <Box component="span">{customer.csecondaryemail}</Box>
              </ListItem>
            </>
          )}
          <ListItem id={'qa-help-install-address'}>
            <Box mr={1}>
              <b>Installation Address:</b>
            </Box>
            <Box component="span">
              {customer.caddress1}, {customer.ccity}, {customer.cstate} {customer.czip}
            </Box>
          </ListItem>
        </List>
        <List id="qa-loan-info" className="help-ids-list">
          <Divider textAlign="left">
            <b>LOAN INFO</b>
          </Divider>
          <ListItem id={'qa-help-loan-apr'}>
            <Box mr={1}>
              <b>APR:</b>
            </Box>
            <Box component="span">{financingMethod?.apr}</Box>
          </ListItem>
          <ListItem id={'qa-help-loan-amount'}>
            <Box mr={1}>
              <b>Loan Amount:</b>
            </Box>
            <Box component="span">{financingMethod?.amountFinanced}</Box>
          </ListItem>
          <ListItem id={'qa-help-loan-type'}>
            <Box mr={1}>
              <b>Loan Type:</b>
            </Box>
            <Box component="span">{financingMethod?.loanType}</Box>
          </ListItem>
          <ListItem id={'qa-help-loan-term'}>
            <Box mr={1}>
              <b>Term:</b>
            </Box>
            <Box component="span">{financingMethod?.term}</Box>
          </ListItem>
        </List>
        <List id="qa-tier-2-ids" className="help-ids-list">
          <Divider textAlign="left">
            <b>TIER 2 IDs</b>
          </Divider>
          <ListItem id={'qa-help-case-id'}>
            <Box mr={1}>
              <b>Bonita Case ID:</b>
            </Box>
            <Box component="span">{eligibility ? eligibility?.creditCheck?.financingProducts[0]?.externalId : ''}</Box>
          </ListItem>
          <ListItem id={'qa-help-coid'}>
            <Box mr={1}>
              <b>COID:</b>
            </Box>
            <Box component="span">{proposal?.coid}</Box>
          </ListItem>
          <ListItem id={'qa-help-frid'}>
            <Box mr={1}>
              <b>FRID:</b>
            </Box>
            <Box component="span">{financingRequest?.id}</Box>
          </ListItem>
          <ListItem id={'qa-help-fmid'}>
            <Box mr={1}>
              <b>FMID:</b>
            </Box>
            <Box component="span">{financingMethod?.id}</Box>
          </ListItem>
          <ListItem id={'qa-help-propid'}>
            <Box mr={1}>
              <b>Proposal ID:</b>
            </Box>
            <Box component="span">{proposal?.id}</Box>
          </ListItem>
          <ListItem id={'qa-help-userid'}>
            <Box mr={1}>
              <b>User ID:</b>
            </Box>
            <Box component="span">{installer?.userid}</Box>
          </ListItem>
          {financingRequest?.steps.map((step, i) => {
            return (
              <ListItem id={`qa-help-frs-ids-${step.externalSku}`}>
                <Box mr={1}>
                  <b>Step IDs {step.externalSku}:</b>
                </Box>
                <Box component="span">
                  Step ID [{step.id}] Doc IDs [{step?.docs?.map((doc) => doc.id)?.join(', ')}]
                </Box>
              </ListItem>
            );
          })}
          {financingRequest?.steps.map((step, i) => {
            return (
              <ListItem id={`qa-help-frsd-rev-${step.externalSku}`}>
                <Box mr={1}>
                  <b>CurrentRevision IDs {step.externalSku}:</b>
                </Box>
                <Box component="span">{step?.docs?.map((doc) => doc?.currentRevision?.id)?.join(', ')}</Box>
              </ListItem>
            );
          })}
          {financingRequest?.steps.map((step, i) => {
            return (
              <ListItem id={`qa-help-envelope-id-${step.externalSku}`}>
                <Box mr={1}>
                  <b>Envelopes {step.externalSku}:</b>
                </Box>
                <Box component="span">
                  {step?.docs
                    ?.map((doc) => doc?.currentRevision?.envelopeId)
                    ?.filter((id) => id)
                    ?.join(', ')}
                </Box>
              </ListItem>
            );
          })}
        </List>
      </Box>
      <Box display="flex" justifyContent="center">
        <button className={'link-button'} onClick={onCloseClick}>
          Close
        </button>
      </Box>
    </Box>
  );
};

export default HelpIdsTable;
